import MessageCircleWhatsappIcon from '@components/v2/icons/MessageCircleWhatsapp';
import ErrorScreen from '@components/v2/layout/ErrorScreen/ErrorScreen';
import Typography from '@components/v2/ui/Typography/Typography';
import { RefreshCw } from 'lucide-react';
import type { FallbackProps } from 'react-error-boundary';
import LoadingPage from '../../../../components/LoadingPage/LoadingPage';
import LangEnum from '../../../../lang/LangEnum';
import { getLintToTeceoWhatsappWithHelpText } from '../../../../utils/constants';
import { OUR_BRAND_NAME } from '../../../reports/utils/consts';
import { useTranslation } from '../../../translation/TranslationContext';
import useErrorBoundaryFallbackPage from './hooks/useErrorBoundaryFallbackPage';

const STATUS_CODE_FONT_SIZE = 120;
const STATUS_CODE_FONT_WEIGHT = 300;

const ErrorBoundaryFallbackPage = ({ error }: FallbackProps) => {
  const { translate } = useTranslation();

  const { goToHomeWithReload, isValidatingOnlineStatus, isOutdatedProblemWithAutoReload, errorId } =
    useErrorBoundaryFallbackPage({ error });

  if (isValidatingOnlineStatus) {
    return <LoadingPage />;
  }

  const reloadLabel = isOutdatedProblemWithAutoReload
    ? LangEnum.GLOBAL_WE_WILL_RESTART_YOUR_BROWSER
    : LangEnum.GLOBAL_RELOAD_THE_PAGE;

  return (
    <ErrorScreen
      title={{ text: LangEnum.FIVE_HUNDRED_ERROR_TITLE }}
      description={{ text: LangEnum.FIVE_HUNDRED_ERROR_DESCRIPTION }}
      note={errorId ?? undefined}
      illustration={
        <Typography
          component="span"
          color="primary.light"
          sx={{
            fontSize: STATUS_CODE_FONT_SIZE,
            fontWeight: STATUS_CODE_FONT_WEIGHT,
            lineHeight: 1,
          }}
        >
          500
        </Typography>
      }
      actions={{
        secondary: {
          label: LangEnum.GLOBAL_TALK_TO_BRAND,
          labelValues: { brand: OUR_BRAND_NAME },
          icon: MessageCircleWhatsappIcon,
          href: getLintToTeceoWhatsappWithHelpText(translate, errorId ?? ''),
        },
        primary: {
          label: reloadLabel,
          icon: RefreshCw,
          isLoading: isOutdatedProblemWithAutoReload,
          disabled: isOutdatedProblemWithAutoReload,
          onClick: goToHomeWithReload,
        },
      }}
    />
  );
};

export default ErrorBoundaryFallbackPage;
